/* Native responsive image viewer. The product gallery remains the source of truth. */
import { setupAnioSwipe } from './anio-swipe.js';

class Anio6MobileGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller || !this.closest('main[data-anio-template="product.anio-6"]')) return;
    this.trigger = this.querySelector('[data-gallery-expand]');
    this.dialog = this.querySelector('dialog');
    this.image = this.querySelector('[data-gallery-expanded-image]');
    this.zoom = this.querySelector('[data-gallery-zoom-toggle]');
    this.closeButton = this.querySelector('[data-gallery-close]');
    this.navigationButtons = [...this.querySelectorAll('[data-gallery-viewer-direction]')];
    if (!this.trigger || !this.dialog || !this.image || !this.zoom || !this.closeButton) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.trigger.addEventListener('click', () => this.open(), { signal });
    this.closeButton.addEventListener('click', () => this.dialog.close(), { signal });
    this.navigationButtons.forEach(button => {
      button.addEventListener('click', () => this.moveImage(Number(button.dataset.galleryViewerDirection)), { signal });
    });
    this.dialog.addEventListener('keydown', event => {
      if (!this.dialog.open || event.altKey || event.ctrlKey || event.metaKey ||
          !['ArrowLeft', 'ArrowRight'].includes(event.key) ||
          (this.closest('anio-product')?.gallery?.length || 0) < 2) return;
      event.preventDefault();
      this.moveImage(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.zoom.addEventListener('click', () => {
      this.setZoom(this.zoom.getAttribute('aria-pressed') !== 'true');
    }, { signal });
    setupAnioSwipe(this.zoom, {
      onSwipe: direction => this.moveImage(direction),
      signal,
      enabled: () => this.dialog.open && this.zoom.getAttribute('aria-pressed') !== 'true' &&
        (this.closest('anio-product')?.gallery?.length || 0) > 1,
    });
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }, { signal });
    // Native dialog handles Escape and traps focus. Restore the opener for
    // every close path, including Escape, backdrop click and the close button.
    this.dialog.addEventListener('close', () => {
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.isConnected) this.trigger.focus({ preventScroll: true });
    }, { signal });
    this.dataset.ready = '';
  }

  syncImage() {
    const product = this.closest('anio-product');
    const singleImage = (product?.gallery?.length || 0) < 2;
    this.navigationButtons.forEach(button => { button.hidden = singleImage; });
    const active = product?.querySelector('.ap-gallery [data-gallery-slide]:not([hidden]) img');
    if (!active?.src) return false;
    // The source src is Shopify's full gallery rendition (up to 2000 px),
    // rather than the smaller responsive thumbnail currently selected by srcset.
    this.dialog.dataset.galleryPresentation = active.closest('[data-gallery-slide]')?.dataset.galleryPresentation || 'cutout';
    this.image.src = active.src;
    this.image.alt = active.alt || 'Anio 6 Produktbild';
    return true;
  }

  setZoom(enlarged) {
    this.zoom.setAttribute('aria-pressed', String(enlarged));
    this.zoom.setAttribute('aria-label', enlarged ? 'Produktbild verkleinern' : 'Produktbild vergrößern');
    this.zoom.style.touchAction = enlarged ? 'auto' : 'pan-y pinch-zoom';
    if (!enlarged) this.zoom.parentElement.scrollTo({ left: 0, top: 0 });
  }

  moveImage(direction) {
    const product = this.closest('anio-product');
    if (!product?.gallery || product.gallery.length < 2 || typeof product.showImage !== 'function') return;
    product.showImage(product.galleryIndex + direction);
    this.syncImage();
    this.zoom.parentElement.scrollTo({ left: 0, top: 0 });
  }

  open() {
    if (this.dialog.open || !this.syncImage()) return;
    this.setZoom(false);
    this.dialog.showModal();
    this.zoom.parentElement.scrollTo({ left: 0, top: 0 });
    this.trigger.setAttribute('aria-expanded', 'true');
    this.closeButton.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.controller?.abort();
    this.controller = null;
    delete this.dataset.ready;
  }
}

if (!customElements.get('anio6-mobile-gallery')) {
  customElements.define('anio6-mobile-gallery', Anio6MobileGallery);
}
